import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Anchor,
  Ship, 
  MapPin, 
  User, 
  Phone, 
  Users, 
  Calendar, 
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Moon,
  Loader2,
  CheckCircle2,
  LocateFixed,
  Car,
  Locate,
  Lock,
  Eye,
  EyeOff,
  LogIn,
  UserPlus
} from "lucide-react";
import axios from "axios";
import PhoneInput, { getLanguageFromCountryCode, getCountryFromPhone } from "@/components/PhoneInput";
import { API_URL } from "@/config/api";

// Simple map component using OpenStreetMap
const MiniMap = ({ address, onAddressDetected }) => {
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 49.49437, lng: 0.107929 }); // Le Havre default

  // Load Leaflet CSS
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (mapLoaded && mapRef.current && window.L) {
      // Check if map already exists
      if (mapRef.current._leaflet_id) {
        return;
      }

      const map = window.L.map(mapRef.current).setView([mapCenter.lat, mapCenter.lng], 13);
      
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(map);

      const marker = window.L.marker([mapCenter.lat, mapCenter.lng]).addTo(map);

      // Store map and marker for updates
      mapRef.current._map = map;
      mapRef.current._marker = marker;
    }
  }, [mapLoaded, mapCenter]);

  // Update map when address changes via geocoding
  const updateMapFromAddress = useCallback(async (addressText) => {
    if (!addressText || addressText.length < 5) return;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressText)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data[0]) {
        const { lat, lon } = data[0];
        setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lon) });
        
        if (mapRef.current?._map && mapRef.current?._marker) {
          mapRef.current._map.setView([lat, lon], 15);
          mapRef.current._marker.setLatLng([lat, lon]);
        }
      }
    } catch (error) {
      console.log("Geocoding error:", error);
    }
  }, []);

  // Detect current location
  const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setDetecting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter({ lat: latitude, lng: longitude });

        if (mapRef.current?._map && mapRef.current?._marker) {
          mapRef.current._map.setView([latitude, longitude], 15);
          mapRef.current._marker.setLatLng([latitude, longitude]);
        }

        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          
          if (data && data.display_name) {
            onAddressDetected(data.display_name);
            toast.success("Location detected successfully");
          }
        } catch (error) {
          console.error("Reverse geocoding error:", error);
          toast.error("Could not get address from location");
        }
        
        setDetecting(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        toast.error("Could not detect your location. Please enter address manually.");
        setDetecting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Update map when address prop changes
  useEffect(() => {
    if (address && address.length > 10) {
      const timeoutId = setTimeout(() => {
        updateMapFromAddress(address);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [address, updateMapFromAddress]);

  return (
    <div className="space-y-3 relative" style={{ zIndex: 1 }}>
      <div 
        ref={mapRef} 
        className="h-48 w-full rounded-md border border-slate-200 bg-slate-100"
        style={{ minHeight: '192px', position: 'relative', zIndex: 1 }}
      />
      <Button
        type="button"
        variant="outline"
        onClick={detectLocation}
        disabled={detecting}
        className="w-full"
        data-testid="detect-location-btn"
      >
        {detecting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Detecting location...
          </>
        ) : (
          <>
            <LocateFixed className="w-4 h-4 mr-2" />
            Detect My Location
          </>
        )}
      </Button>
      <p className="text-xs text-slate-500 text-center">
        Click to auto-detect your location, or enter address manually below
      </p>
    </div>
  );
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const [priceData, setPriceData] = useState(null);
  const [suggestedTerminal, setSuggestedTerminal] = useState(null);
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, loading, success, denied, error
  const [existingBooking, setExistingBooking] = useState(null); // For handling return from Stripe
  const [resumeMode, setResumeMode] = useState(false); // Show resume booking UI
  
  // Client account states
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  
  // Get current date and time for defaults (Europe/Paris timezone)
  const getDefaultDate = () => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  };
  
  const getDefaultTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  
  const [formData, setFormData] = useState({
    ship_name: "",
    terminal_id: "",
    pickup_address: "",
    passenger_name: "",
    phone_number: "+33",  // Default to France country code
    client_email: "",
    passengers: 1,
    pickup_date: getDefaultDate(),
    pickup_time: getDefaultTime(),
    payment_method: "pay_now",
    driver_notes: "",
    // Round trip fields
    is_round_trip: false,
    return_date: "",
    return_time: ""
  });
  
  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for existing booking (return from Stripe cancel)
  // Check if client is already logged in
  useEffect(() => {
    const token = localStorage.getItem("client_token");
    const storedClientInfo = localStorage.getItem("client_info");
    
    if (token && storedClientInfo) {
      // Verify token is still valid
      axios.get(`${API_URL}/api/client/verify-token`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(response => {
        if (response.data.valid) {
          setIsLoggedIn(true);
          setClientInfo(response.data.client);
          // Pre-fill form with client info
          const client = response.data.client;
          setFormData(prev => ({
            ...prev,
            passenger_name: client.name || prev.passenger_name,
            phone_number: client.phone || prev.phone_number,
            client_email: client.email || prev.client_email
          }));
        }
      }).catch(() => {
        // Token invalid, clear storage
        localStorage.removeItem("client_token");
        localStorage.removeItem("client_info");
      });
    }
  }, []);

  useEffect(() => {
    const bookingId = searchParams.get('booking_id');
    if (bookingId) {
      // User returned from Stripe - fetch the existing booking
      const fetchExistingBooking = async () => {
        try {
          const response = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
          const booking = response.data;
          
          // Only show resume if payment is still pending
          if (booking.payment_status === 'pending') {
            setExistingBooking(booking);
            setResumeMode(true);
          }
        } catch (error) {
          console.error("Failed to fetch existing booking:", error);
          // Booking not found or error - continue with fresh form
        }
      };
      fetchExistingBooking();
    }
  }, [searchParams]);

  // Fetch terminals
  useEffect(() => {
    const fetchTerminals = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/terminals`);
        setTerminals(response.data);
      } catch (error) {
        console.error("Failed to fetch terminals:", error);
        toast.error("Failed to load terminals");
      }
    };
    fetchTerminals();
  }, []);

  // Geofencing: Suggest terminal based on user location
  const requestLocationAndSuggestTerminal = async () => {
    if (!navigator.geolocation) {
      setLocationStatus("error");
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setLocationStatus("loading");
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await axios.post(`${API_URL}/api/suggest-terminal`, {
            latitude,
            longitude
          });
          
          if (response.data.suggested_terminal) {
            setSuggestedTerminal(response.data.suggested_terminal);
            setLocationStatus("success");
            toast.success(`Nearest terminal found: ${response.data.suggested_terminal.name}`);
          } else {
            setLocationStatus("error");
            if (response.data.reason === "too_far") {
              toast.info("You appear to be far from the port. Please select your terminal manually.");
            }
          }
        } catch (error) {
          console.error("Terminal suggestion failed:", error);
          setLocationStatus("error");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationStatus("denied");
          toast.info("Location access denied. Please select your terminal manually.");
        } else {
          setLocationStatus("error");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Apply suggested terminal
  const applySuggestedTerminal = () => {
    if (suggestedTerminal) {
      handleChange("terminal_id", suggestedTerminal.id);
      toast.success(`Terminal set to ${suggestedTerminal.name}`);
    }
  };

  // Calculate price when relevant fields change - PRICE IS PER VEHICLE
  useEffect(() => {
    const calculatePrice = async () => {
      if (!formData.terminal_id || !formData.pickup_date || !formData.pickup_time) {
        return;
      }

      const pickupDatetime = `${formData.pickup_date}T${formData.pickup_time}:00`;
      
      // Prepare return time if round trip
      let returnDatetime = null;
      if (formData.is_round_trip && formData.return_date && formData.return_time) {
        returnDatetime = `${formData.return_date}T${formData.return_time}:00`;
      }
      
      try {
        const response = await axios.post(`${API_URL}/api/calculate-price`, {
          terminal_id: formData.terminal_id,
          passengers: parseInt(formData.passengers),
          pickup_time: pickupDatetime,
          is_round_trip: formData.is_round_trip,
          return_time: returnDatetime
        });
        setPriceData(response.data);
      } catch (error) {
        console.error("Price calculation failed:", error);
      }
    };
    calculatePrice();
  }, [formData.terminal_id, formData.pickup_date, formData.pickup_time, formData.is_round_trip, formData.return_date, formData.return_time]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressDetected = (address) => {
    setFormData(prev => ({ ...prev, pickup_address: address }));
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.terminal_id && formData.ship_name && formData.pickup_address;
      case 2:
        // Passengers must be a valid number >= 1
        const passengersValid = formData.passengers !== "" && parseInt(formData.passengers) >= 1;
        return formData.passenger_name && formData.phone_number && passengersValid && isValidEmail(formData.client_email);
      case 3:
        // If round trip, validate return date/time
        if (formData.is_round_trip) {
          return formData.pickup_date && formData.pickup_time && formData.return_date && formData.return_time;
        }
        return formData.pickup_date && formData.pickup_time;
      case 4:
        return formData.payment_method;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (isStepValid()) {
      // Show account prompt when leaving step 2 if not logged in
      if (step === 2 && !isLoggedIn && !showAccountPrompt) {
        setShowAccountPrompt(true);
        return;
      }
      setStep(prev => prev + 1);
    }
  };

  const handleSkipAccountCreation = () => {
    setShowAccountPrompt(false);
    setStep(prev => prev + 1);
  };

  const handleCreateAccount = async () => {
    if (accountPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (accountPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setCreatingAccount(true);
    try {
      const response = await axios.post(`${API_URL}/api/client/register`, {
        email: formData.client_email,
        password: accountPassword,
        name: formData.passenger_name,
        phone: formData.phone_number
      });

      // Store token and client info
      localStorage.setItem("client_token", response.data.token);
      localStorage.setItem("client_info", JSON.stringify(response.data.client));
      
      setIsLoggedIn(true);
      setClientInfo(response.data.client);
      setShowAccountPrompt(false);
      
      toast.success("Account created! You can now track all your bookings.");
      setStep(prev => prev + 1);
    } catch (error) {
      console.error("Account creation error:", error);
      if (error.response?.status === 400) {
        // Email already registered - offer to login instead
        toast.error("This email is already registered. Please login to your account.");
      } else {
        toast.error("Failed to create account. You can continue without one.");
      }
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleBack = () => {
    if (showAccountPrompt) {
      setShowAccountPrompt(false);
      return;
    }
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const pickupDatetime = `${formData.pickup_date}T${formData.pickup_time}:00`;
      
      // Prepare return datetime if round trip
      let returnDatetime = null;
      if (formData.is_round_trip && formData.return_date && formData.return_time) {
        returnDatetime = `${formData.return_date}T${formData.return_time}:00`;
      }
      
      // Detect client language from phone number
      const clientLanguage = getCountryFromPhone(formData.phone_number)?.lang || "en";
      
      const bookingData = {
        ship_name: formData.ship_name,
        terminal_id: formData.terminal_id,
        pickup_address: formData.pickup_address,
        passenger_name: formData.passenger_name,
        phone_number: formData.phone_number,
        client_email: formData.client_email,
        passengers: parseInt(formData.passengers),
        pickup_datetime: pickupDatetime,
        payment_method: formData.payment_method,
        driver_notes: formData.driver_notes || null,
        // Round trip fields
        is_round_trip: formData.is_round_trip,
        return_datetime: returnDatetime,
        // Client language for notifications
        client_language: clientLanguage
      };

      const response = await axios.post(`${API_URL}/api/bookings`, bookingData);
      const booking = response.data;

      if (formData.payment_method === "pay_now") {
        // Create Stripe checkout session
        const checkoutResponse = await axios.post(`${API_URL}/api/create-checkout`, {
          booking_id: booking.booking_id,
          origin_url: window.location.origin
        });
        
        // Redirect to Stripe
        window.location.href = checkoutResponse.data.url;
      } else {
        // Pay to driver - redirect to success
        window.location.href = `/booking/success?booking_id=${booking.booking_id}`;
      }
    } catch (error) {
      console.error("Booking failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  // Resume payment for existing booking
  const handleResumePayment = async (paymentMethod) => {
    if (!existingBooking) return;
    
    setLoading(true);
    try {
      if (paymentMethod === "pay_now") {
        // Create new Stripe checkout session for existing booking
        const checkoutResponse = await axios.post(`${API_URL}/api/create-checkout`, {
          booking_id: existingBooking.booking_id,
          origin_url: window.location.origin
        });
        window.location.href = checkoutResponse.data.url;
      } else {
        // Switch to cash payment - redirect to success
        window.location.href = `/booking/success?booking_id=${existingBooking.booking_id}`;
      }
    } catch (error) {
      console.error("Resume payment failed:", error);
      toast.error("Failed to process payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Start fresh booking (abandon existing)
  const handleStartFresh = () => {
    setExistingBooking(null);
    setResumeMode(false);
    // Clear the booking_id from URL
    window.history.replaceState({}, '', '/booking');
  };

  const selectedTerminal = terminals.find(t => t.id === formData.terminal_id);

  // Show Resume Booking UI if returning from Stripe cancel
  if (resumeMode && existingBooking) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-slate-900 text-white py-4">
          <div className="container-app">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                  <Anchor className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-black text-white tracking-tight">Y GO</span>
              </Link>
            </div>
          </div>
        </header>

        <div className="container-app py-8">
          <div className="max-w-lg mx-auto">
            <Card className="shadow-lg">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Complete Your Booking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <p className="text-sm text-slate-600">
                    <strong>Booking Reference:</strong> {existingBooking.booking_id}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Terminal:</strong> {existingBooking.terminal_name}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Passenger:</strong> {existingBooking.passenger_name}
                  </p>
                  <p className="text-lg font-bold text-slate-900 mt-3">
                    Total: €{existingBooking.final_price?.toFixed(2)}
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-center text-slate-600">
                    How would you like to pay?
                  </p>
                  
                  <Button
                    onClick={() => handleResumePayment("pay_now")}
                    disabled={loading}
                    className="w-full bg-orange-500 hover:bg-orange-600 py-6"
                    data-testid="resume-pay-card-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CreditCard className="w-5 h-5 mr-2" />
                    )}
                    Pay by Card (Secure Online Payment)
                  </Button>
                  
                  <Button
                    onClick={() => handleResumePayment("pay_driver")}
                    disabled={loading}
                    variant="outline"
                    className="w-full py-6"
                    data-testid="resume-pay-cash-btn"
                  >
                    Pay Cash to Driver
                  </Button>
                </div>

                <Separator />

                <Button
                  onClick={handleStartFresh}
                  variant="ghost"
                  className="w-full text-slate-500"
                >
                  Start a New Booking
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="container-app">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-orange-500 flex items-center justify-center">
                <Anchor className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-black text-white tracking-tight">Y GO</span>
            </Link>
            <Link to="/" className="text-slate-300 hover:text-white text-sm font-medium flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="container-app py-8">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`step-indicator ${
                    s < step ? 'step-completed' : s === step ? 'step-active' : 'step-inactive'
                  }`}
                >
                  {s < step ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 sm:w-20 h-0.5 mx-1 ${s < step ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Labels */}
          <div className="flex justify-between mb-8 text-xs sm:text-sm text-slate-500 px-1">
            <span className={step >= 1 ? 'text-slate-900 font-medium' : ''}>Route & Address</span>
            <span className={step >= 2 ? 'text-slate-900 font-medium' : ''}>Details</span>
            <span className={step >= 3 ? 'text-slate-900 font-medium' : ''}>Date & Time</span>
            <span className={step >= 4 ? 'text-slate-900 font-medium' : ''}>Payment</span>
          </div>

          {/* Step 1: Route Selection with Address & Map */}
          {step === 1 && (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  Select Terminal & Pickup Location
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="ship_name" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Ship Name
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Enter the name of your vessel</p>
                  <div className="relative mt-2">
                    <Ship className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="ship_name"
                      data-testid="ship-name-input"
                      placeholder="e.g., MV Ocean Star, MSC Aurora..."
                      value={formData.ship_name}
                      onChange={(e) => handleChange("ship_name", e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Terminal Where Your Ship is Docked
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Select the port terminal where we pick you up (or drop you off)</p>
                  
                  {/* Geolocation suggestion */}
                  {locationStatus === "idle" && !formData.terminal_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={requestLocationAndSuggestTerminal}
                      className="mb-3 text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="detect-terminal-btn"
                    >
                      <Locate className="w-4 h-4 mr-2" />
                      Detect Nearest Terminal
                    </Button>
                  )}
                  
                  {locationStatus === "loading" && (
                    <div className="flex items-center gap-2 mb-3 text-blue-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Detecting your location...</span>
                    </div>
                  )}
                  
                  {suggestedTerminal && !formData.terminal_id && (
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            Suggested: {suggestedTerminal.name}
                          </p>
                          <p className="text-xs text-green-600">
                            {suggestedTerminal.distance_km} km from your location
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={applySuggestedTerminal}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Use This
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <Select 
                    value={formData.terminal_id} 
                    onValueChange={(value) => handleChange("terminal_id", value)}
                  >
                    <SelectTrigger data-testid="terminal-select" className="h-12 relative z-20">
                      <SelectValue placeholder="Select pickup terminal" />
                    </SelectTrigger>
                    <SelectContent className="z-50">
                      {terminals.map((terminal) => (
                        <SelectItem key={terminal.id} value={terminal.id}>
                          <div className="flex justify-between items-center w-full gap-4">
                            <span>{terminal.name}</span>
                            <span className="text-orange-500 font-mono font-bold">€{terminal.price}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedTerminal && (
                  <div className="price-card">
                    <div className="flex items-center gap-2 mb-2">
                      <Car className="w-5 h-5 text-orange-500" />
                      <p className="text-sm font-bold text-slate-700">Price per vehicle</p>
                    </div>
                    <p className="text-3xl font-mono font-bold text-slate-900">
                      €{selectedTerminal.price.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Not per person — one price for the entire vehicle</p>
                  </div>
                )}

                <Separator />

                {/* Pickup Address with Map */}
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3 block">
                    Pickup Location
                  </Label>
                  
                  {/* Mini Map */}
                  <MiniMap 
                    address={formData.pickup_address} 
                    onAddressDetected={handleAddressDetected}
                  />

                  <div className="mt-4">
                    <Label htmlFor="pickup_address" className="text-sm font-medium text-slate-600">
                      Pickup Address <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                      <textarea
                        id="pickup_address"
                        data-testid="pickup-address-input"
                        placeholder="Enter your pickup address (or use location detection above)"
                        value={formData.pickup_address}
                        onChange={(e) => handleChange("pickup_address", e.target.value)}
                        className="w-full pl-10 pr-4 py-3 min-h-[80px] border border-slate-200 rounded-md focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 resize-none"
                        rows={2}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      This address will be shared with your driver
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Passenger Details */}
          {step === 2 && !showAccountPrompt && (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-orange-500" />
                  Passenger Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="passenger_name" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Full Name
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      id="passenger_name"
                      data-testid="passenger-name-input"
                      placeholder="Enter passenger name"
                      value={formData.passenger_name}
                      onChange={(e) => handleChange("passenger_name", e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone_number" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Phone Number (WhatsApp)
                  </Label>
                  <div className="mt-2">
                    <PhoneInput
                      value={formData.phone_number}
                      onChange={(phone) => handleChange("phone_number", phone)}
                      placeholder="6 12 34 56 78"
                      className="h-12"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">We&apos;ll send your confirmation via WhatsApp</p>
                </div>

                <div>
                  <Label htmlFor="client_email" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-2">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <Input
                      id="client_email"
                      data-testid="email-input"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.client_email}
                      onChange={(e) => handleChange("client_email", e.target.value)}
                      className={`pl-10 h-12 ${formData.client_email && !isValidEmail(formData.client_email) ? 'border-red-500 focus:border-red-500' : ''}`}
                    />
                  </div>
                  {formData.client_email && !isValidEmail(formData.client_email) && (
                    <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
                  )}
                  {(!formData.client_email || isValidEmail(formData.client_email)) && (
                    <p className="text-xs text-slate-500 mt-1">We&apos;ll send your booking confirmation to this email</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="passengers" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Number of Passengers
                  </Label>
                  <div className="flex items-center gap-2 mt-2">
                    {/* Decrement button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleChange("passengers", Math.max(1, (parseInt(formData.passengers) || 1) - 1))}
                      disabled={formData.passengers <= 1}
                      className="h-12 w-12 shrink-0"
                      data-testid="passengers-decrement"
                    >
                      <span className="text-xl font-bold">−</span>
                    </Button>
                    
                    {/* Input field - editable */}
                    <div className="relative flex-1">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="passengers"
                        data-testid="passengers-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.passengers === 0 ? "" : formData.passengers}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow empty string for editing, or valid numbers
                          if (val === "") {
                            handleChange("passengers", "");
                          } else {
                            const num = parseInt(val, 10);
                            if (!isNaN(num) && num >= 0 && num <= 99) {
                              handleChange("passengers", num);
                            }
                          }
                        }}
                        onBlur={(e) => {
                          // On blur, ensure minimum value of 1
                          const val = parseInt(e.target.value, 10);
                          if (isNaN(val) || val < 1) {
                            handleChange("passengers", 1);
                          }
                        }}
                        className="pl-10 h-12 text-center text-lg font-semibold"
                        style={{ appearance: 'textfield' }}
                      />
                    </div>
                    
                    {/* Increment button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => handleChange("passengers", Math.min(99, (parseInt(formData.passengers) || 0) + 1))}
                      className="h-12 w-12 shrink-0"
                      data-testid="passengers-increment"
                    >
                      <span className="text-xl font-bold">+</span>
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    For vehicle planning only — price is per vehicle, not per person
                  </p>
                </div>

                <div>
                  <Label htmlFor="driver_notes" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Additional Information for the Driver (Optional)
                  </Label>
                  <textarea
                    id="driver_notes"
                    data-testid="driver-notes-input"
                    placeholder="Gate number, meeting point, special instructions, timing notes..."
                    value={formData.driver_notes}
                    onChange={(e) => handleChange("driver_notes", e.target.value)}
                    className="w-full mt-2 px-4 py-3 min-h-[80px] border border-slate-200 rounded-md focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 resize-none text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Any details to help your driver find you (optional)
                  </p>
                </div>

                {/* Logged in indicator */}
                {isLoggedIn && clientInfo && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Logged in as {clientInfo.name}</p>
                        <p className="text-xs text-green-600">Your booking will be linked to your account</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Login prompt for existing users */}
                {!isLoggedIn && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <LogIn className="w-5 h-5 text-slate-500" />
                        <p className="text-sm text-slate-600">Already have an account?</p>
                      </div>
                      <Link to="/client/login" className="text-sm font-medium text-orange-600 hover:text-orange-700">
                        Sign in
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Account Creation Prompt - shown after Step 2 for non-logged in users */}
          {step === 2 && showAccountPrompt && !isLoggedIn && (
            <Card className="animate-fade-in">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-orange-600" />
                </div>
                <CardTitle className="text-xl">Create Your Account</CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  Save your booking history and track all your rides in one place
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Access your booking history anytime</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Track your driver from your dashboard</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Download receipts for all your trips</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Get notified when you&apos;re near a terminal</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="account-password">Choose a Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="account-password"
                        type="password"
                        placeholder="Minimum 6 characters"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        className="pl-10 h-12"
                        data-testid="account-password"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="account-confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="account-confirm-password"
                        type="password"
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 h-12"
                        data-testid="account-confirm-password"
                      />
                      {confirmPassword && accountPassword === confirmPassword && (
                        <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleCreateAccount}
                    disabled={creatingAccount || !accountPassword || !confirmPassword}
                    className="w-full bg-orange-500 hover:bg-orange-600 py-6"
                    data-testid="create-account-btn"
                  >
                    {creatingAccount ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Create Account & Continue
                      </>
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={handleSkipAccountCreation}
                    className="text-slate-500 hover:text-slate-700"
                    data-testid="skip-account-btn"
                  >
                    Skip for now, continue without account
                  </Button>
                </div>

                <p className="text-xs text-slate-500 text-center">
                  Your information ({formData.client_email}) will be used to create your account
                </p>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Date & Time */}
          {step === 3 && (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-orange-500" />
                  Pickup Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="pickup_date" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Pickup Date (Outbound)
                  </Label>
                  <Input
                    id="pickup_date"
                    data-testid="pickup-date-input"
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => handleChange("pickup_date", e.target.value)}
                    className="h-12 mt-2"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div>
                  <Label htmlFor="pickup_time" className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                    Pickup Time (Outbound)
                  </Label>
                  <Input
                    id="pickup_time"
                    data-testid="pickup-time-input"
                    type="time"
                    value={formData.pickup_time}
                    onChange={(e) => handleChange("pickup_time", e.target.value)}
                    className="h-12 mt-2"
                  />
                </div>

                {priceData?.night_surcharge_applied && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <Moon className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Night Service</p>
                      <p className="text-xs text-amber-600">+20% surcharge applies (21:00 - 06:00)</p>
                    </div>
                  </div>
                )}

                <Separator className="my-4" />

                {/* Round Trip Option */}
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="is_round_trip"
                      checked={formData.is_round_trip}
                      onChange={(e) => handleChange("is_round_trip", e.target.checked)}
                      className="mt-1 h-5 w-5 text-green-600 rounded border-green-300 focus:ring-green-500"
                      data-testid="round-trip-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_round_trip" className="text-base font-bold text-green-800 cursor-pointer">
                        Add Return Trip
                      </Label>
                      <p className="text-sm text-green-700 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600 text-white text-xs font-bold mr-2">
                          -10% OFF
                        </span>
                        Save 10% on return trip when you pay now!
                      </p>
                    </div>
                  </div>
                  
                  {formData.is_round_trip && (
                    <div className="mt-4 pt-4 border-t border-green-200 space-y-4">
                      <div>
                        <Label htmlFor="return_date" className="text-sm font-bold text-green-800 uppercase tracking-wider">
                          Return Date
                        </Label>
                        <Input
                          id="return_date"
                          data-testid="return-date-input"
                          type="date"
                          value={formData.return_date}
                          onChange={(e) => handleChange("return_date", e.target.value)}
                          className="h-12 mt-2 border-green-300 focus:border-green-500"
                          min={formData.pickup_date || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="return_time" className="text-sm font-bold text-green-800 uppercase tracking-wider">
                          Return Time
                        </Label>
                        <Input
                          id="return_time"
                          data-testid="return-time-input"
                          type="time"
                          value={formData.return_time}
                          onChange={(e) => handleChange("return_time", e.target.value)}
                          className="h-12 mt-2 border-green-300 focus:border-green-500"
                        />
                      </div>
                      
                      {priceData?.return_night_surcharge_applied && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                          <Moon className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="text-sm font-medium text-amber-800">Return Night Service</p>
                            <p className="text-xs text-amber-600">+20% surcharge applies for return (21:00 - 06:00)</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Price Summary */}
                {priceData && formData.is_round_trip && priceData.return_final_price && (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <h4 className="font-bold text-slate-800 mb-3">Price Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Outbound trip:</span>
                        <span className="font-mono">€{priceData.final_price_eur.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-700">
                        <span>Return trip (10% off):</span>
                        <span className="font-mono">
                          <span className="line-through text-slate-400 mr-2">€{priceData.return_price_before_discount?.toFixed(2)}</span>
                          €{priceData.return_final_price.toFixed(2)}
                        </span>
                      </div>
                      <Separator />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total:</span>
                        <span className="font-mono text-orange-600">€{priceData.total_price_eur?.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-green-600 font-medium text-right">
                        You save €{priceData.return_discount_amount?.toFixed(2)} on return!
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 4: Payment */}
          {step === 4 && (
            <Card className="animate-fade-in">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-orange-500" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => handleChange("payment_method", value)}
                  className="space-y-3"
                >
                  <div className={`flex items-center space-x-3 p-4 border rounded-md cursor-pointer transition-colors ${
                    formData.payment_method === "pay_now" ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"
                  }`}>
                    <RadioGroupItem value="pay_now" id="pay_now" data-testid="pay-now-radio" />
                    <Label htmlFor="pay_now" className="flex-1 cursor-pointer">
                      <div className="font-medium">Pay Now</div>
                      <div className="text-sm text-slate-500">Secure payment via Stripe</div>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-3 p-4 border rounded-md cursor-pointer transition-colors ${
                    formData.payment_method === "pay_to_driver" ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"
                  }`}>
                    <RadioGroupItem value="pay_to_driver" id="pay_to_driver" data-testid="pay-driver-radio" />
                    <Label htmlFor="pay_to_driver" className="flex-1 cursor-pointer">
                      <div className="font-medium">Pay to Driver</div>
                      <div className="text-sm text-slate-500">Cash payment on arrival</div>
                    </Label>
                  </div>
                </RadioGroup>

                <Separator />

                {/* Booking Summary */}
                <div className="price-card space-y-3">
                  <h3 className="font-bold text-slate-900">Booking Summary</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Terminal:</span>
                      <span className="font-medium">{selectedTerminal?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ship:</span>
                      <span className="font-medium">{formData.ship_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Pickup Address:</span>
                      <span className="font-medium text-right max-w-[200px] truncate" title={formData.pickup_address}>
                        {formData.pickup_address.substring(0, 40)}...
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Passengers:</span>
                      <span className="font-medium">{formData.passengers} (info only)</span>
                    </div>
                    {formData.driver_notes && (
                      <div className="flex justify-between">
                        <span className="text-slate-500">Driver Notes:</span>
                        <span className="font-medium text-right max-w-[200px] truncate" title={formData.driver_notes}>
                          {formData.driver_notes.substring(0, 30)}...
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-slate-500">Pickup:</span>
                      <span className="font-medium">
                        {formData.pickup_date} at {formData.pickup_time}
                      </span>
                    </div>
                    {formData.is_round_trip && formData.return_date && formData.return_time && (
                      <div className="flex justify-between text-green-700">
                        <span>Return:</span>
                        <span className="font-medium">
                          {formData.return_date} at {formData.return_time}
                        </span>
                      </div>
                    )}
                  </div>

                  <Separator />

                  {priceData && (
                    <div className="space-y-2">
                      {/* Vehicle price indicator */}
                      <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 rounded-md">
                        <Car className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">Price per vehicle — not per person</span>
                      </div>
                      
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Outbound trip</span>
                        <span>€{priceData.base_price.toFixed(2)}</span>
                      </div>
                      {priceData.night_surcharge_applied && (
                        <div className="flex justify-between text-sm text-amber-600">
                          <span className="flex items-center gap-1">
                            <Moon className="w-4 h-4" />
                            Night surcharge (+20%)
                          </span>
                          <span>€{priceData.night_surcharge.toFixed(2)}</span>
                        </div>
                      )}
                      
                      {/* Round trip pricing */}
                      {formData.is_round_trip && priceData.return_final_price && (
                        <>
                          <Separator className="my-2" />
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Return trip</span>
                            <span>€{priceData.return_base_price?.toFixed(2)}</span>
                          </div>
                          {priceData.return_night_surcharge_applied && (
                            <div className="flex justify-between text-sm text-amber-600">
                              <span className="flex items-center gap-1">
                                <Moon className="w-4 h-4" />
                                Return night (+20%)
                              </span>
                              <span>€{priceData.return_night_surcharge?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm text-green-600">
                            <span className="font-medium">10% Return Discount</span>
                            <span>-€{priceData.return_discount_amount?.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      
                      <Separator />
                      <div className="flex justify-between items-baseline">
                        <span className="font-bold">
                          {formData.is_round_trip ? "Total (Round Trip)" : "Total (per vehicle)"}
                        </span>
                        <div className="text-right">
                          <p className="text-2xl font-mono font-bold text-orange-500">
                            €{formData.is_round_trip && priceData.total_price_eur 
                              ? priceData.total_price_eur.toFixed(2) 
                              : priceData.final_price_eur.toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-500">
                            ≈ ${formData.is_round_trip && priceData.total_price_usd 
                              ? priceData.total_price_usd.toFixed(2) 
                              : priceData.final_price_usd.toFixed(2)} USD
                          </p>
                          {formData.is_round_trip && priceData.return_discount_amount && (
                            <p className="text-xs text-green-600 font-medium mt-1">
                              You save €{priceData.return_discount_amount.toFixed(2)}!
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-500 text-center">
                  By booking, you agree to our{" "}
                  <Link to="/terms" className="text-orange-500 hover:underline">
                    Terms & Conditions
                  </Link>
                </p>
              </CardContent>
            </Card>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            {step > 1 ? (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 4 ? (
              <Button 
                data-testid="next-step-btn"
                onClick={handleNext}
                disabled={!isStepValid()}
                className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button 
                data-testid="submit-booking-btn"
                onClick={handleSubmit}
                disabled={loading || !isStepValid()}
                className="bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    {formData.payment_method === "pay_now" ? "Proceed to Payment" : "Confirm Booking"}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
